import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";
import { SPECIALTIES } from "../lib/collab";
import { toast } from "sonner";
import { X, Users } from "lucide-react";

export default function RequestSpecialistInputModal({ analysisId, open, onClose }) {
  const nav = useNavigate();
  const [picked, setPicked] = useState({});
  const [otherText, setOtherText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const toggle = (v) => setPicked((p) => ({ ...p, [v]: !p[v] }));

  const submit = async () => {
    const invites = [];
    SPECIALTIES.forEach((s) => {
      if (s.value === "other") return;
      if (picked[s.value]) invites.push({ specialty: s.value });
    });
    if (picked.other) {
      const txt = otherText.trim();
      if (!txt) {
        toast.error("Please name the other specialty");
        return;
      }
      invites.push({ specialty: "other", specialty_other: txt });
    }
    if (!invites.length) {
      toast.error("Select at least one specialist");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/collab/cases", {
        case_analysis_id: analysisId,
        invites,
      });
      toast.success("Collaboration case created");
      nav(`/collab-cases/${r.data.id}`);
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Could not start collaboration"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="collab-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[var(--pa-surface)] border border-[var(--pa-gold)]/40 rounded-lg p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-[var(--pa-gold)]" />
            <h2 className="font-serif text-xl text-[var(--pa-text)]">
              Request Specialist Input
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="collab-modal-close"
            className="p-1 rounded hover:bg-[var(--pa-bg-2)] text-[var(--pa-text-dim)] hover:text-[var(--pa-text)]"
          >
            <X size={16} />
          </button>
        </div>
        <p className="text-xs text-[var(--pa-text-muted)] mt-2 leading-relaxed">
          Select which specialists you want input from. ProsthoAI generates a unique secure
          link for each one — the link expires in 30 days. The specialist does not need a
          ProsthoAI account.
        </p>

        <div className="mt-5 space-y-2" data-testid="collab-modal-list">
          {SPECIALTIES.map((s) => (
            <label
              key={s.value}
              data-testid={`collab-modal-${s.value}`}
              className={`flex items-center gap-2.5 p-2.5 rounded-md border text-sm cursor-pointer transition ${
                picked[s.value]
                  ? "border-[var(--pa-gold)] bg-[var(--pa-gold)]/5"
                  : "border-[var(--pa-border)] hover:border-[var(--pa-gold)]/60"
              }`}
            >
              <input
                type="checkbox"
                checked={!!picked[s.value]}
                onChange={() => toggle(s.value)}
                className="accent-[var(--pa-gold)]"
              />
              <span className="text-[var(--pa-text)]">{s.label}</span>
            </label>
          ))}
          {picked.other && (
            <input
              type="text"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              data-testid="collab-modal-other-text"
              placeholder="Name the specialty (e.g. Pediatric Dentist)"
              className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2.5"
            />
          )}
        </div>

        <button
          onClick={submit}
          disabled={submitting}
          data-testid="collab-modal-submit"
          className="mt-5 w-full bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-5 py-2.5 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-60"
        >
          {submitting ? "Creating…" : "Generate secure specialist links"}
        </button>
      </div>
    </div>
  );
}
